/* Plantica — WhatsApp button: stay clear of what is fixed to the bottom.

   The product page's sticky add-to-cart bar on a phone, the cookie banner, an
   app's teaser: anything fixed to the bottom edge, under the button's column,
   lifts it by its own height. When it goes, the button comes back down.

   Cheap on purpose — it runs on scroll and every half second, for things that
   come and go on their own (the banner answered, the bar sliding in). It does
   not walk the page: it asks the browser what lies under a few points in the
   button's column (`elementsFromPoint`) — the bottom edge, for bars glued to
   it, and the button's own spot, for what floats a little above the edge, as
   the cookie banner does — and climbs from each to the fixed box holding it.

   Full-screen layers (overlays, open modals) are not obstacles: they cover the
   button on purpose, from a higher z-index.

   Everything is measured where it comes to rest, not where it is drawn
   (06.10.2026). The button's own place comes from its stylesheet offset, and a
   bar is taken at its resting place, its slide taken out. Measured as drawn,
   mid-way through a slide — the button's own, or a bar's — the numbers chased
   each other, and the button bobbed up and down for a second and a half before
   it settled.

   Custom file. See CUSTOMIZATIONS.md. */

(() => {
  if (customElements.get('plantica-whatsapp')) return;

  class PlanticaWhatsapp extends HTMLElement {
    connectedCallback() {
      this.frame = 0;
      this.onChange = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.place();
        });
      };
      window.addEventListener('scroll', this.onChange, { passive: true });
      window.addEventListener('resize', this.onChange);
      this.timer = setInterval(this.onChange, 500);
      this.place();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onChange);
      window.removeEventListener('resize', this.onChange);
      clearInterval(this.timer);
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    place() {
      const height = window.innerHeight;
      const rect = this.getBoundingClientRect();
      if (!rect.width) return;

      // Where the button sits with no lift at all: the stylesheet's offset from
      // the bottom edge, not the position it is drawn at.
      const naturalBottom = height - this.offset();
      const naturalTop = naturalBottom - rect.height;
      const x = Math.round(rect.left + rect.width / 2);
      const GAP = 12;

      // Probe the bottom edge (bars glued to it) and the button's own spot
      // (things that float a little above the edge, like the cookie banner),
      // then again wherever the lift has moved it, until nothing fixed is in
      // the way — a banner can sit above a bar.
      let lift = 0;
      for (let round = 0; round < 4; round += 1) {
        const top = naturalTop - lift;
        const bottom = naturalBottom - lift;
        const ys = round === 0 ? [height - 2, bottom - 2, (top + bottom) / 2, top + 2] : [bottom - 2, (top + bottom) / 2, top + 2];
        let needed = lift;
        for (const y of ys) {
          if (y < 0) continue;
          for (const top of this.obstaclesAt(x, y, height)) {
            needed = Math.max(needed, naturalBottom - top + GAP);
          }
        }
        if (needed === lift) break;
        lift = needed;
      }

      const value = `${Math.round(lift)}px`;
      if (this.style.getPropertyValue('--plantica-wa-lift') !== value) {
        this.style.setProperty('--plantica-wa-lift', value);
      }
    }

    // `--plantica-wa-offset` in pixels: 1.6rem on a phone, 2.4rem from 750px.
    offset() {
      const value = getComputedStyle(this).getPropertyValue('--plantica-wa-offset').trim();
      const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
      const number = parseFloat(value);
      if (Number.isNaN(number)) return 16;
      return value.endsWith('rem') ? number * root : number;
    }

    // The tops of the fixed boxes under a point, other than this button and
    // full-screen layers — each where it comes to rest: a bar sliding in or out
    // is drawn shifted by its transform, which is taken back out.
    obstaclesAt(x, y, height) {
      const found = [];
      const seen = new Set();
      for (const hit of document.elementsFromPoint(x, y)) {
        if (hit === this || this.contains(hit)) continue;
        for (let node = hit; node && node !== document.body && node !== document.documentElement; node = node.parentElement) {
          if (seen.has(node)) break;
          seen.add(node);
          if (getComputedStyle(node).position !== 'fixed') continue;
          const box = node.getBoundingClientRect();
          if (box.height > 0 && box.height <= height * 0.6) {
            const transform = getComputedStyle(node).transform;
            const shift = transform && transform !== 'none' ? new DOMMatrixReadOnly(transform).m42 : 0;
            found.push(box.top - shift);
          }
          break;
        }
      }
      return found;
    }
  }

  customElements.define('plantica-whatsapp', PlanticaWhatsapp);
})();
